/**
 * CameraController - Advanced cinematic camera system
 * Modes:
 * - FREE: Traditional orbit controls around scene center
 * - FOCUS: Orbit around selected object (Mercury, Earth, EAHP, etc.)
 * - POV: First-person view from inside a Starship cockpit
 * - CHASE: Third-person camera following behind a Starship
 * - CINEMATIC: Automatic sweeping cinematic camera movements
 */

import { useRef, useEffect, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';
import { useStore, CameraMode, CameraTarget } from '../../store/useStore';

// === PRE-ALLOCATED VECTORS FOR PERFORMANCE ===
const _cockpitOffset = new THREE.Vector3();
const _cockpitPos = new THREE.Vector3();
const _lookAhead = new THREE.Vector3();
const _chaseOffset = new THREE.Vector3();
const _chasePos = new THREE.Vector3();
const _lookTarget = new THREE.Vector3();
const _interpPos = new THREE.Vector3();
const _interpTarget = new THREE.Vector3();
const _tempOffset = new THREE.Vector3();

// Scene positions (must match App.tsx)
const POSITIONS = {
  sun: new THREE.Vector3(-30, 0, 0),
  mercury: new THREE.Vector3(-2, 0, 15),
  earth: new THREE.Vector3(50, 0, 0),
  eahp: new THREE.Vector3(-30, 0, 0), // Sun position (EAHP orbits sun)
  geoReceiver: new THREE.Vector3(54, 3, 5),
};

interface CameraControllerProps {
  // Reference to get Starship positions from parent
  starshipPositions?: React.MutableRefObject<THREE.Vector3[]>;
  starshipDirections?: React.MutableRefObject<THREE.Vector3[]>;
}

export function CameraController({
  starshipPositions,
  starshipDirections,
}: CameraControllerProps) {
  const { camera, gl } = useThree();
  const controlsRef = useRef<any>(null);
  const cinematicTimeRef = useRef(0);
  const smoothPosRef = useRef(new THREE.Vector3(20, 45, 110));
  const smoothTargetRef = useRef(new THREE.Vector3(0, 0, 0));

  const {
    view: { cameraMode, cameraTarget, followShipIndex },
    isPaused,
    animationSpeed,
  } = useStore();

  // Get target position based on cameraTarget
  const getTargetPosition = (): THREE.Vector3 => {
    switch (cameraTarget) {
      case 'sun':
        return POSITIONS.sun.clone();
      case 'mercury':
        return POSITIONS.mercury.clone();
      case 'earth':
        return POSITIONS.earth.clone();
      case 'eahp':
        return POSITIONS.sun.clone().add(new THREE.Vector3(8, 0, 0)); // EAHP orbit
      case 'geoReceiver':
        return POSITIONS.geoReceiver.clone();
      case 'starship':
        if (starshipPositions?.current?.[followShipIndex]) {
          return starshipPositions.current[followShipIndex].clone();
        }
        return POSITIONS.earth.clone();
      default:
        return new THREE.Vector3(10, 0, 10);
    }
  };

  // Calculate focus orbit distance based on target
  const getFocusDistance = (): number => {
    switch (cameraTarget) {
      case 'sun':
        return 20;
      case 'mercury':
        return 8;
      case 'earth':
        return 6;
      case 'eahp':
        return 15;
      case 'geoReceiver':
        return 5;
      case 'starship':
        return 3;
      default:
        return 10;
    }
  };

  // Cinematic camera path keyframes
  const cinematicKeyframes = useMemo(() => [
    // Wide establishing shot
    { pos: new THREE.Vector3(20, 60, 120), target: new THREE.Vector3(0, 0, 20), duration: 8 },
    // Sun close-up
    { pos: new THREE.Vector3(-15, 10, 15), target: POSITIONS.sun, duration: 6 },
    // EAHP swarm fly-through
    { pos: new THREE.Vector3(-22, 5, 8), target: new THREE.Vector3(-22, 0, 0), duration: 5 },
    // Mercury approach
    { pos: new THREE.Vector3(5, 8, 20), target: POSITIONS.mercury, duration: 6 },
    // Laser beam following shot
    { pos: new THREE.Vector3(15, 15, 10), target: new THREE.Vector3(25, 5, 5), duration: 7 },
    // Earth approach
    { pos: new THREE.Vector3(45, 10, 15), target: POSITIONS.earth, duration: 6 },
    // GEO station detail
    { pos: new THREE.Vector3(52, 5, 8), target: POSITIONS.geoReceiver, duration: 5 },
    // Grand overview
    { pos: new THREE.Vector3(30, 80, 80), target: new THREE.Vector3(10, 0, 10), duration: 10 },
  ], []);

  useFrame((state, delta) => {
    if (!camera) return;

    const effectiveDelta = isPaused ? 0 : delta * animationSpeed;

    switch (cameraMode) {
      case 'free':
        // Standard orbit controls - handled by OrbitControls component
        if (controlsRef.current) {
          controlsRef.current.target.set(10, 0, 10);
        }
        break;

      case 'focus':
        // Orbit around selected target
        if (controlsRef.current && cameraTarget) {
          const targetPos = getTargetPosition();
          // Smooth transition to new target
          smoothTargetRef.current.lerp(targetPos, 0.05);
          controlsRef.current.target.copy(smoothTargetRef.current);
          controlsRef.current.minDistance = getFocusDistance() * 0.5;
          controlsRef.current.maxDistance = getFocusDistance() * 3;
        }
        break;

      case 'pov':
        // First-person view from Starship cockpit
        if (starshipPositions?.current?.[followShipIndex] && starshipDirections?.current?.[followShipIndex]) {
          const shipPos = starshipPositions.current[followShipIndex];
          const shipDir = starshipDirections.current[followShipIndex];

          // Position camera at nose of ship (cockpit view)
          // PERFORMANCE: Use pre-allocated vectors
          _cockpitOffset.copy(shipDir).multiplyScalar(0.5);
          _cockpitPos.copy(shipPos).add(_cockpitOffset);
          _cockpitPos.y += 0.1; // Slight elevation for pilot view

          // Look ahead in direction of travel
          _tempOffset.copy(shipDir).multiplyScalar(10);
          _lookAhead.copy(shipPos).add(_tempOffset);

          // Smooth camera movement
          smoothPosRef.current.lerp(_cockpitPos, 0.1);
          smoothTargetRef.current.lerp(_lookAhead, 0.1);

          camera.position.copy(smoothPosRef.current);
          camera.lookAt(smoothTargetRef.current);

          // Disable orbit controls in POV mode
          if (controlsRef.current) {
            controlsRef.current.enabled = false;
          }
        }
        break;

      case 'chase':
        // Third-person chase camera behind Starship
        if (starshipPositions?.current?.[followShipIndex] && starshipDirections?.current?.[followShipIndex]) {
          const shipPos = starshipPositions.current[followShipIndex];
          const shipDir = starshipDirections.current[followShipIndex];

          // Position camera behind and above ship
          // PERFORMANCE: Use pre-allocated vectors
          _chaseOffset.copy(shipDir).multiplyScalar(-3); // Behind
          _chaseOffset.y += 1.5; // Above
          _chasePos.copy(shipPos).add(_chaseOffset);

          // Look at ship (slightly ahead)
          _tempOffset.copy(shipDir).multiplyScalar(2);
          _lookTarget.copy(shipPos).add(_tempOffset);

          // Smooth camera movement
          smoothPosRef.current.lerp(_chasePos, 0.08);
          smoothTargetRef.current.lerp(_lookTarget, 0.08);

          camera.position.copy(smoothPosRef.current);
          camera.lookAt(smoothTargetRef.current);

          // Disable orbit controls in chase mode
          if (controlsRef.current) {
            controlsRef.current.enabled = false;
          }
        }
        break;

      case 'cinematic':
        // Automatic cinematic camera movement
        cinematicTimeRef.current += effectiveDelta;

        // Calculate total cycle duration
        const totalDuration = cinematicKeyframes.reduce((sum, kf) => sum + kf.duration, 0);
        const cycleTime = cinematicTimeRef.current % totalDuration;

        // Find current keyframe
        let accumulated = 0;
        let currentIdx = 0;
        for (let i = 0; i < cinematicKeyframes.length; i++) {
          if (cycleTime < accumulated + cinematicKeyframes[i].duration) {
            currentIdx = i;
            break;
          }
          accumulated += cinematicKeyframes[i].duration;
        }

        const currentKf = cinematicKeyframes[currentIdx];
        const nextKf = cinematicKeyframes[(currentIdx + 1) % cinematicKeyframes.length];
        const localT = (cycleTime - accumulated) / currentKf.duration;

        // Smooth interpolation using smoothstep
        const smoothT = localT * localT * (3 - 2 * localT);

        // Interpolate position and target
        // PERFORMANCE: Use pre-allocated vectors
        _interpPos.copy(currentKf.pos).lerp(nextKf.pos, smoothT);
        _interpTarget.copy(currentKf.target).lerp(nextKf.target, smoothT);

        // Apply with additional smoothing
        smoothPosRef.current.lerp(_interpPos, 0.03);
        smoothTargetRef.current.lerp(_interpTarget, 0.03);

        camera.position.copy(smoothPosRef.current);
        camera.lookAt(smoothTargetRef.current);

        // Disable orbit controls in cinematic mode
        if (controlsRef.current) {
          controlsRef.current.enabled = false;
        }
        break;
    }

    // Re-enable controls for modes that use them
    if (controlsRef.current && (cameraMode === 'free' || cameraMode === 'focus')) {
      controlsRef.current.enabled = true;
      controlsRef.current.update();
    }
  });

  // Reset camera when mode changes
  useEffect(() => {
    if (cameraMode === 'free') {
      smoothPosRef.current.set(20, 45, 110);
      smoothTargetRef.current.set(10, 0, 10);
    } else if (cameraMode === 'focus' && cameraTarget) {
      const targetPos = getTargetPosition();
      const distance = getFocusDistance();
      smoothPosRef.current.copy(targetPos).add(new THREE.Vector3(distance, distance * 0.5, distance));
      smoothTargetRef.current.copy(targetPos);
    }
  }, [cameraMode, cameraTarget]);

  return (
    <>
      <PerspectiveCamera
        makeDefault
        position={[20, 45, 110]}
        fov={60}
        near={0.1}
        far={1000}
      />

      <OrbitControls
        ref={controlsRef}
        enableDamping
        dampingFactor={0.05}
        minDistance={2}
        maxDistance={350}
        autoRotate={!isPaused && cameraMode === 'free'}
        autoRotateSpeed={0.25 * animationSpeed}
        maxPolarAngle={Math.PI * 0.85}
      />
    </>
  );
}

// Hook to provide starship tracking data
export function useStarshipTracking() {
  const positionsRef = useRef<THREE.Vector3[]>([]);
  const directionsRef = useRef<THREE.Vector3[]>([]);

  const updateShip = (index: number, position: THREE.Vector3, direction: THREE.Vector3) => {
    positionsRef.current[index] = position.clone();
    directionsRef.current[index] = direction.clone();
  };

  return {
    positionsRef,
    directionsRef,
    updateShip,
  };
}
